import { nextTick } from 'vue'
import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
} from 'vue-router'

import { useAuthStore } from '@/stores/auth'

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { layout: 'minimal', requiresGuest: true, title: 'Iniciar sesión' },
  },
  {
    path: '/olvide',
    name: 'olvide',
    component: () => import('@/views/OlvideView.vue'),
    meta: { layout: 'minimal', requiresGuest: true, title: 'Recuperar contraseña' },
  },
  {
    path: '/restablecer',
    name: 'restablecer',
    component: () => import('@/views/RestablecerView.vue'),
    meta: { layout: 'minimal', title: 'Nueva contraseña' },
  },
  {
    path: '/',
    name: 'pos',
    component: () => import('@/views/POSView.vue'),
    meta: { requiresAuth: true, title: 'POS' },
  },
  {
    path: '/cuenta',
    name: 'cuenta',
    component: () => import('@/views/CuentaView.vue'),
    meta: { requiresAuth: true, title: 'Mi cuenta' },
  },
  {
    path: '/turnos',
    name: 'turnos',
    component: () => import('@/views/TurnosView.vue'),
    meta: { requiresAuth: true, title: 'Turnos' },
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/views/DashboardView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Dashboard' },
  },
  {
    path: '/historial',
    name: 'admin-ventas',
    component: () => import('@/views/AdminVentasView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Historial' },
  },
  {
    path: '/productos',
    name: 'admin-productos',
    component: () => import('@/views/ProductosView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: 'Productos' },
  },
  // Administración: una sola entrada en el menú, con pestañas. Cada pestaña
  // conserva su URL. El meta del padre aplica a todas las hijas.
  {
    path: '/admin',
    component: () => import('@/layouts/AdminLayout.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
    redirect: { name: 'admin-usuarios' },
    children: [
      {
        path: 'usuarios',
        name: 'admin-usuarios',
        component: () => import('@/views/UsuariosView.vue'),
        meta: { title: 'Usuarios' },
      },
      {
        path: 'configuracion',
        name: 'admin-configuracion',
        component: () => import('@/views/ConfiguracionView.vue'),
        // Los datos fiscales se editan en su propia pestaña (Facturación).
        props: { excluirGrupos: ['Facturación'] },
        meta: { title: 'Configuración' },
      },
      {
        path: 'facturacion',
        name: 'admin-facturacion',
        component: () => import('@/views/FacturacionView.vue'),
        meta: { title: 'Facturación' },
      },
      {
        path: 'reportes',
        name: 'admin-reportes',
        component: () => import('@/views/ReportesView.vue'),
        meta: { title: 'Reportes' },
      },
      {
        path: 'auditoria',
        name: 'admin-auditoria',
        component: () => import('@/views/AuditoriaView.vue'),
        meta: { title: 'Auditoría' },
      },
      {
        path: 'estado',
        name: 'admin-estado',
        component: () => import('@/views/EstadoView.vue'),
        meta: { title: 'Estado' },
      },
      {
        path: 'molino',
        name: 'admin-molino',
        component: () => import('@/views/MolinoView.vue'),
        meta: { title: 'Molino' },
      },
    ],
  },
  // Rutas antiguas: no romper enlaces guardados.
  { path: '/admin/ventas', redirect: { name: 'admin-ventas' } },
  { path: '/admin/productos', redirect: { name: 'admin-productos' } },
  { path: '/molino', redirect: { name: 'admin-molino' } },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/',
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  if (!auth.bootstrapped) {
    await auth.bootstrap()
  }
  document.title = to.meta.title
    ? `${String(to.meta.title)} · Café Ventas`
    : 'Café Ventas'

  if (to.meta.requiresGuest && auth.isAuthenticated) return { name: 'pos' }
  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'login', query: { next: to.fullPath } }
  }
  if (to.meta.requiresAdmin && !auth.isAdmin) return { name: 'pos' }
  return true
})

// Transición suave entre pantallas (View Transitions API, Chrome/Edge/Safari 18).
// Donde no existe, la navegación sigue igual que siempre. La animación vive en
// style.css (::view-transition-*), que también la apaga con reduced-motion.
let terminarTransicion: (() => void) | null = null

router.beforeResolve((to, from) => {
  if (!('startViewTransition' in document) || !from.matched.length || to.path === from.path) return
  const raiz = document.documentElement
  raiz.classList.add('en-transicion')
  return new Promise<void>((seguir) => {
    const vt = document.startViewTransition(
      () =>
        new Promise<void>((listo) => {
          terminarTransicion = listo
          seguir()
        }),
    )
    // Si el navegador aborta la animación, la navegación sigue igual.
    vt.ready.catch(() => {})
    // Con la página oculta el navegador no pinta y `finished` puede tardar; el
    // nombre de transición no debe quedarse puesto (ver style.css).
    const quitar = () => raiz.classList.remove('en-transicion')
    vt.finished.finally(quitar)
    window.setTimeout(quitar, 1000)
  })
})

router.afterEach(async () => {
  if (!terminarTransicion) return
  await nextTick()
  terminarTransicion()
  terminarTransicion = null
})

window.addEventListener('cafe:session-expired', () => {
  const auth = useAuthStore()
  auth.clear()
  router.replace({ name: 'login' })
})
